import {
  DotsHorizontalIcon,
  DownloadIcon,
  FileDownloadIcon,
  QuestionMarkIcon,
  TrashIcon,
  UploadIcon,
} from '@fourthwall/icons'
import { formatBytes } from '@fourthwall/utils/dist/file'
import React from 'react'
import { Upload } from '../../inputs/Upload'
import { useMediaQueryContext } from '../../providers/MediaQueryProvider'
import { Banner } from '../Banner'
import { Dropdown } from '../Dropdown'
import { Icon } from '../Icon'
import { Loader } from '../Loader'
import { ToolbarButton } from '../ToolbarButton'
import { Tooltip } from '../Tooltip'
import * as S from './styled'
import type { FilesListDataItem, Props } from './types'

export const FilesList = ({
  accept,
  maxSize,
  banner,
  data,
  disabled = false,
  onFileDrop,
  onFileDropRejected,
  onFileRemove,
  onFileDownload,
}: Props) => {
  const media = useMediaQueryContext()

  const renderActions = (file: FilesListDataItem) => {
    if (!onFileRemove && !onFileDownload) return null

    const isNewlyAddedFile = 'isUploading' in file

    return (
      <Dropdown
        content={
          <>
            {onFileRemove && (
              <Dropdown.Item
                label="Delete file"
                leftIcon={TrashIcon}
                onClick={() => onFileRemove(file)}
                testid="FilesList.DeleteOption"
              />
            )}
            {onFileDownload && !isNewlyAddedFile && (
              <Dropdown.Item
                label="Download file"
                leftIcon={DownloadIcon}
                onClick={() => onFileDownload(file)}
                testid="FilesList.DownloadOption"
              />
            )}
          </>
        }
        position="bottom-end"
      >
        <ToolbarButton
          size="small"
          icon={DotsHorizontalIcon}
          disabled={disabled}
          testid="FilesList.DropdownButton"
        />
      </Dropdown>
    )
  }

  const renderFile = (file: FilesListDataItem) => {
    return (
      <S.FileWrapper key={file.id}>
        <S.File>
          <S.FileIcon>
            <Icon component={FileDownloadIcon} height={24} />
          </S.FileIcon>
          <S.FileName>
            <S.FileNameWrapper>{file.name}</S.FileNameWrapper>
          </S.FileName>
          <S.FileSize $withoutMargin={disabled}>{file.size}</S.FileSize>

          {file.isUploading ? (
            <S.LoaderWrapper>
              <Loader size="small" />
            </S.LoaderWrapper>
          ) : (
            renderActions(file)
          )}
        </S.File>
        {file.hasLocationMetadata && (
          <S.FileWarning>
            This file contains sensitive location data{' '}
            <Tooltip reference={<Icon component={QuestionMarkIcon} height={14} />}>
              Anyone who downloads this file will be able to view the EXIF data, containing details
              about the location where the image was captured.
            </Tooltip>
          </S.FileWarning>
        )}
      </S.FileWrapper>
    )
  }

  const renderTitle = () => {
    const max = maxSize ? ` (${formatBytes(maxSize, { decimals: 0 })})` : null

    return (
      <span>
        {media.tablet ? (
          <>
            Drop new file or <u>browse</u>
          </>
        ) : (
          'Browse'
        )}
        {max && <span>{max}</span>}
      </span>
    )
  }

  if (!data) {
    return null
  }

  return (
    <div>
      {banner && (
        <S.BannerWrapper>
          <Banner {...banner} />
        </S.BannerWrapper>
      )}
      <div>{data.map(renderFile)}</div>
      <Upload
        accept={accept}
        maxSize={maxSize}
        title={renderTitle()}
        icon={UploadIcon}
        iconSize={24}
        orientation="horizontal"
        disabled={disabled}
        onFileDrop={onFileDrop}
        onFileDropRejected={onFileDropRejected}
      />
    </div>
  )
}
